import { moveToFulfilled, update } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { TopicsAllLoadAction, TopicsAllLoadMoreAction } from '../../domains/engageTopicsAll/actions.types';
import type { TopicHeaderLoadAction } from '../../domains/topicHeaderLoad/actions.types';
import type { TopicCreateFulfilledAction } from '../../domains/topics/createActions.types';
import type { TopicEditAction } from '../../domains/topics/editActions.types';
import type { TopicsFollowedLoadAction } from '../../domains/topicsFollowedLoad/loadActions.types';
import type { TopicsFollowedLoadMoreAction } from '../../domains/topicsFollowedLoad/loadMoreActions.types';
import type { TrendingTopicsLoadAction } from '../../domains/trendingTopicsLoad/loadActions.types';
import type { TrendingTopicsLoadMoreAction } from '../../domains/trendingTopicsLoad/loadMoreActions.types';
import type { TypeaheadFulfilledAction } from '../../domains/typeahead/actions.types';
import type { Topic } from '../topics/types';

import type { TopicDescription, TopicDescriptions } from './types';

export type TopicDescriptionsAction =
  | TopicEditAction
  | TopicHeaderLoadAction
  | TrendingTopicsLoadAction
  | TrendingTopicsLoadMoreAction
  | TopicsAllLoadAction
  | TopicsAllLoadMoreAction
  | TopicsFollowedLoadAction
  | TopicsFollowedLoadMoreAction
  | TypeaheadFulfilledAction
  | TopicCreateFulfilledAction;

const initialState: TopicDescriptions = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const updateTopics = (state: TopicDescriptions, topics: Record<Topic['id'], Topic>) => {
  const topicsIds = Object.keys(topics);

  const topicDescriptions = topicsIds.reduce<Record<TopicDescription['id'], TopicDescription>>(
    (accum, topicId: Topic['id']) => ({
      ...accum,
      [topicId]: {
        id: topicId,
        description: topics[topicId].description || '',
        shortDescription: topics[topicId].shortDescription,
      },
    }),
    {}
  );

  return moveToFulfilled(state, topicDescriptions);
};

const reducer: Reducer<TopicDescriptions, TopicDescriptionsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'TOPIC.CREATE_FULFILLED':
      return updateTopics(state, action.payload.entities.topics);
    case 'TOPIC.EDIT_FULFILLED':
      return update(state, action.payload.topicId, { description: action.payload.topic.description });
    case 'TOPIC_HEADER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicDescriptions);
    case 'TOPICS_ALL.LOAD_FULFILLED':
      return updateTopics(state, action.payload.entities.topics);
    case 'TOPICS_ALL.LOAD_MORE_FULFILLED':
      return updateTopics(state, action.payload.entities.topics);
    case 'TRENDING_TOPICS.LOAD_FULFILLED':
      return updateTopics(state, action.payload.entities.topics);
    case 'TRENDING_TOPICS.LOAD_MORE_FULFILLED':
      return updateTopics(state, action.payload.entities.topics);
    case 'TOPICS_FOLLOWED.LOAD_FULFILLED':
      return updateTopics(state, action.payload.entities.topics);
    case 'TOPICS_FOLLOWED.LOAD_MORE_FULFILLED':
      return updateTopics(state, action.payload.entities.topics);
    case 'TYPEAHEAD.LOAD_FULFILLED':
      return updateTopics(state, action.payload.entities.topics);
    default:
      return state;
  }
};

export default reducer;
